import type {
  Property,
  Payment,
  PaymentSummary,
  MaintenanceIssue,
  Tradesman,
  QuoteRequest,
  Reminder,
  InboundEmail,
  User,
  DashboardData,
  BlobDocument,
  Message,
  PropertyExpense,
  MaintenanceDetail,
  TradesmanDetail,
  ChartData,
  PaymentTimeline,
  SettingsInfo,
} from './types';

declare const __API_BASE__: string;
const API_BASE = typeof __API_BASE__ !== 'undefined' ? __API_BASE__ : 'https://ipm-prod-func-7quxpmnoih4oo.azurewebsites.net';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

function getHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('token');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const opts: RequestInit = { method, headers: getHeaders() };
  if (body !== undefined) opts.body = JSON.stringify(body);
  const res = await fetch(`${API_BASE}${path}`, opts);
  if (res.status === 401) {
    localStorage.removeItem('token');
    window.location.hash = '#/login';
    throw new ApiError(401, 'Unauthorized');
  }
  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, text || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

async function uploadFile(path: string, formData: FormData): Promise<unknown> {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem('token');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { method: 'POST', headers, body: formData });
  if (res.status === 401) {
    localStorage.removeItem('token');
    window.location.hash = '#/login';
    throw new ApiError(401, 'Unauthorized');
  }
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json();
}

// Auth
export const auth = {
  login: (username: string, password: string) =>
    request<{ token: string; user: User }>('POST', '/auth/login', { username, password }),
  me: () => request<{ user: User }>('GET', '/auth/me').then(r => r.user),

  invite: (data: { email: string; name: string; role: string }) =>
    request<{ message: string }>('POST', '/auth/invite', data),
  register: (data: { token: string; username: string; password: string; phone: string }) =>
    request<{ token: string; user: User }>('POST', '/auth/register', data),
  updateProfile: (data: { current_password: string; email?: string; new_password?: string; new_username?: string }) =>
    request<{ token: string; user: User }>('PUT', '/auth/profile', data),
};

// Dashboard
export const dashboard = {
  get: () => request<DashboardData>('GET', '/dashboard'),
  chart: (monthsBack?: number, monthsFwd?: number, propertyId?: string) => {
    const params = new URLSearchParams();
    if (monthsBack) params.set('months_back', String(monthsBack));
    if (monthsFwd) params.set('months_forward', String(monthsFwd));
    if (propertyId) params.set('property_id', propertyId);
    const qs = params.toString();
    return request<ChartData>('GET', `/dashboard/chart${qs ? `?${qs}` : ''}`);
  },
};

// Properties
export const properties = {
  list: () => request<{ properties: Property[] }>('GET', '/properties').then(r => r.properties),
  get: (id: string) => request<{ property: Property }>('GET', `/properties/${id}`).then(r => r.property),
  create: (data: Partial<Property>) => request<{ property: Property }>('POST', '/properties', data).then(r => r.property),
  update: (id: string, data: Partial<Property>) => request<{ property: Property }>('PUT', `/properties/${id}`, data).then(r => r.property),
  delete: (id: string) => request<void>('DELETE', `/properties/${id}`),
};

// Payments
export const payments = {
  list: (propertyId: string) =>
    request<{ payments: Payment[] }>('GET', `/properties/${propertyId}/payments`).then(r => r.payments),
  summary: (propertyId: string, weeks?: number) =>
    request<{ summary: PaymentSummary }>('GET', `/properties/${propertyId}/payments/summary${weeks ? `?weeks=${weeks}` : ''}`).then(r => r.summary),
  create: (propertyId: string, data: Partial<Payment>) =>
    request<{ payment: Payment }>('POST', `/properties/${propertyId}/payments`, data).then(r => r.payment),
  update: (propertyId: string, id: string, data: Partial<Payment>) =>
    request<{ payment: Payment }>('PUT', `/properties/${propertyId}/payments/${id}`, data).then(r => r.payment),
  delete: (propertyId: string, id: string) =>
    request<void>('DELETE', `/properties/${propertyId}/payments/${id}`),
  timeline: (months?: number) => {
    const qs = months ? `?months=${months}` : '';
    return request<{ timeline: PaymentTimeline[] }>('GET', `/payments/timeline${qs}`).then(r => r.timeline);
  },
};

// Maintenance
export const maintenance = {
  list: (propertyId: string) =>
    request<{ maintenance: MaintenanceIssue[] }>('GET', `/properties/${propertyId}/maintenance`).then(r => r.maintenance),
  create: (propertyId: string, data: Partial<MaintenanceIssue>) =>
    request<{ maintenance: MaintenanceIssue }>('POST', `/properties/${propertyId}/maintenance`, data).then(r => r.maintenance),
  update: (propertyId: string, id: string, data: Partial<MaintenanceIssue>) =>
    request<{ maintenance: MaintenanceIssue }>('PUT', `/properties/${propertyId}/maintenance/${id}`, data).then(r => r.maintenance),
  delete: (propertyId: string, id: string) =>
    request<void>('DELETE', `/properties/${propertyId}/maintenance/${id}`),
  all: (status?: string, priority?: string) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (priority) params.set('priority', priority);
    const qs = params.toString();
    return request<{ maintenance: MaintenanceIssue[] }>('GET', `/maintenance${qs ? `?${qs}` : ''}`).then(r => r.maintenance);
  },
  detail: (propertyId: string, issueId: string) =>
    request<MaintenanceDetail>('GET', `/properties/${propertyId}/maintenance/${issueId}/detail`),
  sendToTradesmen: (propertyId: string, issueId: string, data: { tradesman_ids: string[]; message: string; channel: string }) =>
    request<{ results: unknown[] }>('POST', `/properties/${propertyId}/maintenance/${issueId}/send-to-tradesmen`, data),
  sendToAgent: (propertyId: string, issueId: string, data: { message: string; channel: string }) =>
    request<{ status: string }>('POST', `/properties/${propertyId}/maintenance/${issueId}/send-to-agent`, data),
};

// Documents
export const documents = {
  list: (propertyId: string, category?: string) => {
    const qs = category ? `?category=${category}` : '';
    return request<{ documents: BlobDocument[] }>('GET', `/properties/${propertyId}/documents${qs}`).then(r => r.documents);
  },
  upload: (propertyId: string, file: File, category: string) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', category);
    return uploadFile(`/properties/${propertyId}/documents`, fd);
  },
  download: (propertyId: string, blobName: string) =>
    request<{ download_url: string }>('GET', `/properties/${propertyId}/documents/_blob/download?path=${encodeURIComponent(blobName)}`)
      .then(r => ({ url: r.download_url })),
  delete: (propertyId: string, blobName: string) =>
    request<void>('DELETE', `/properties/${propertyId}/documents/_blob?path=${encodeURIComponent(blobName)}`),
};

// Tradesmen
export const tradesmen = {
  list: () => request<{ tradesmen: Tradesman[] }>('GET', '/tradesmen').then(r => r.tradesmen),
  create: (data: Partial<Tradesman>) => request<{ tradesman: Tradesman }>('POST', '/tradesmen', data).then(r => r.tradesman),
  update: (id: string, data: Partial<Tradesman>) => request<{ tradesman: Tradesman }>('PUT', `/tradesmen/${id}`, data).then(r => r.tradesman),
  delete: (id: string) => request<void>('DELETE', `/tradesmen/${id}`),
  detail: (id: string) => request<TradesmanDetail>('GET', `/tradesmen/${id}/detail`),
  requestQuote: (propertyId: string, issueId: string, tradesmanId: string, data: { message: string; channel: string }) =>
    request<{ results: unknown[] }>('POST', `/tradesmen/${tradesmanId}/quote-request`, { ...data, property_id: propertyId, maintenance_id: issueId }),
  quoteRequests: (propertyId: string, issueId: string) =>
    request<{ quote_requests: QuoteRequest[] }>('GET', `/properties/${propertyId}/maintenance/${issueId}/quotes`).then(r => r.quote_requests),
};

// Reminders
export const reminders = {
  list: (status?: string) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const qs = params.toString();
    return request<{ reminders: Reminder[] }>('GET', `/reminders${qs ? `?${qs}` : ''}`).then(r => r.reminders);
  },
  forProperty: (propertyId: string) =>
    request<{ reminders: Reminder[] }>('GET', `/properties/${propertyId}/reminders`).then(r => r.reminders),
  acknowledge: (id: string) =>
    request<void>('PUT', `/reminders/${id}/acknowledge`),
  delete: (id: string) =>
    request<void>('DELETE', `/reminders/${id}`),
};

// Inbound Emails
export const emails = {
  list: (propertyId?: string) => {
    const qs = propertyId ? `?property_id=${propertyId}` : '';
    return request<{ emails: InboundEmail[] }>('GET', `/inbound-emails${qs}`).then(r => r.emails);
  },
  match: (emailId: string, propertyId: string) =>
    request<{ status: string; email: InboundEmail }>('PUT', `/inbound-emails/${emailId}/match`, { property_id: propertyId }),
  update: (emailId: string, data: Partial<{ category: string; subject: string; property_id: string; parsed_data: unknown }>) =>
    request<{ status: string; email: InboundEmail }>('PUT', `/inbound-emails/${emailId}`, data),
  delete: (emailId: string) =>
    request<{ status: string }>('DELETE', `/inbound-emails/${emailId}`),
  createRecord: (emailId: string, type?: 'payment' | 'maintenance') =>
    request<{ status: string; created: string | null }>('POST', `/inbound-emails/${emailId}/create-record`, type ? { type } : {}),
  rematch: () =>
    request<{ status: string; rematched: number; total_unmatched: number }>('POST', '/email/rematch'),
  reclassify: () =>
    request<{ status: string; reclassified: number; payments_created: number; maintenance_created: number }>('POST', '/email/reclassify'),
};

// PropertyMe Scraper
export const propertyme = {
  sync: (data: { username: string; password: string }) =>
    request<{ properties_synced: number; payments_synced: number; maintenance_synced: number; discrepancies: unknown[] }>('POST', '/propertyme/sync', data),
  start: (data: { username: string; password: string; use_cache?: boolean }) =>
    request<{ job_id: string }>('POST', '/propertyme/sync/start', data),
  status: (jobId: string) =>
    request<{
      status: 'running' | 'complete' | 'error';
      logs: Array<{ time: string; message: string }>;
      result: { properties_synced: number; payments_synced: number; maintenance_synced: number; discrepancies: unknown[]; new_records: unknown[] } | null;
      error: string | null;
    }>('GET', `/propertyme/sync/status/${jobId}`),
};

// Seed Data
export const seed = {
  run: () => request<{ message: string }>('POST', '/seed'),
  purgeDemo: () => request<{ message: string; counts: Record<string, number> }>('DELETE', '/purge-demo'),
};

// Settings info
export const settings = {
  info: () => request<SettingsInfo>('GET', '/settings/info'),
  update: (data: { notification_preference?: string; expected_sender_emails?: string[]; phone?: string; propertyme_username?: string }) =>
    request<{ status: string }>('PUT', '/settings', data),
};

// Messages (conversation threading)
export const messages = {
  list: (contextType: string, contextId: string) =>
    request<{ messages: Message[] }>('GET', `/messages/${contextType}/${contextId}`).then(r => r.messages),
  create: (contextType: string, contextId: string, data: Partial<Message>) =>
    request<{ message: Message }>('POST', `/messages/${contextType}/${contextId}`, data).then(r => r.message),
  sendSms: (contextType: string, contextId: string, to: string, body: string) =>
    request<{ message: Message; sms_result: { status: string; message_sid?: string } }>(
      'POST', `/messages/${contextType}/${contextId}/send-sms`, { to, body }
    ),
};

// Property Expenses
export const expenses = {
  list: (propertyId: string) =>
    request<{ expenses: PropertyExpense[] }>('GET', `/properties/${propertyId}/expenses`).then(r => r.expenses),
  create: (propertyId: string, data: Partial<PropertyExpense>) =>
    request<{ expense: PropertyExpense }>('POST', `/properties/${propertyId}/expenses`, data).then(r => r.expense),
  update: (propertyId: string, id: string, data: Partial<PropertyExpense>) =>
    request<{ expense: PropertyExpense }>('PUT', `/properties/${propertyId}/expenses/${id}`, data).then(r => r.expense),
  delete: (propertyId: string, id: string) =>
    request<void>('DELETE', `/properties/${propertyId}/expenses/${id}`),
};

// Email Ingestion
export const emailIngestion = {
  poll: () => request<{ processed: number; errors: number }>('POST', '/email/poll'),
  status: () => request<{ total_emails: number; processed_count: number; last_poll: string }>('GET', '/email/status'),
};

// Admin – user management (admin role only)
export const admin = {
  listUsers: () =>
    request<{ users: User[] }>('GET', '/mgmt/users').then(r => r.users),
  createUser: (data: { username: string; password: string; email: string; phone: string; name: string; role: string }) =>
    request<{ user: User }>('POST', '/mgmt/users', data).then(r => r.user),
};
